/* =================================================================
   AVORA WISHLIST
   - Manages localStorage state
   - Hydrates product data from Shopify's /products/{handle}.js
   - Renders wishlist page
   - Handles heart button toggles across the site
   ================================================================= */

(function () {
  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  const STORAGE_KEY = 'avora:wishlist';
  const HYDRATED_KEY = 'avora:wishlist:hydrated';
  const HYDRATION_TTL = 60 * 60 * 1000; // 1 hour

  /* ---------- State management ---------- */

  function readWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
      window.dispatchEvent(new CustomEvent('avora:wishlist:change', { detail: { handles } }));
    } catch (e) {
      /* silently ignore */
    }
  }

  function isInWishlist(handle) {
    return readWishlist().includes(handle);
  }

  function addToWishlist(handle) {
    const current = readWishlist();
    if (current.length >= 100) {
      window.dispatchEvent(new CustomEvent('avora:wishlist:full'));
      return false;
    }
    if (!current.includes(handle)) {
      current.push(handle);
      writeWishlist(current);
    }
    return true;
  }

  function removeFromWishlist(handle) {
    const current = readWishlist();
    const next = current.filter((h) => h !== handle);
    writeWishlist(next);
  }

  function toggleWishlist(handle) {
    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
      return false;
    } else {
      return addToWishlist(handle);
    }
  }

  /* ---------- Hydration cache ---------- */

  function readHydrated() {
    try {
      const raw = localStorage.getItem(HYDRATED_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeHydrated(map) {
    try {
      localStorage.setItem(HYDRATED_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  async function hydrateProduct(handle) {
    const cached = readHydrated();
    const entry = cached[handle];
    const now = Date.now();

    if (entry && now - entry.fetched_at < HYDRATION_TTL) {
      return entry;
    }

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) {
        if (res.status === 404) return null;
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();

      const hydrated = {
        handle: data.handle,
        title: data.title,
        image: data.featured_image || (data.images && data.images[0]) || '',
        url: data.url,
        price_min: data.price_min,
        price_max: data.price_max,
        currency: window.Shopify?.currency?.active || 'EUR',
        available: data.available,
        vendor: data.vendor,
        variants: data.variants,
        first_available_variant_id: (data.variants || []).find((v) => v.available)?.id || null,
        fetched_at: now,
      };

      cached[handle] = hydrated;
      writeHydrated(cached);
      return hydrated;
    } catch (e) {
      return entry || null;
    }
  }

  /* ---------- Server sync (logged-in) ---------- */

  function mergeServerWishlist() {
    const serverData = document.querySelector('[data-wishlist-server-data]');
    if (!serverData) return;

    try {
      const parsed = JSON.parse(serverData.textContent);
      const serverList = Array.isArray(parsed.customer_wishlist) ? parsed.customer_wishlist : [];
      if (serverList.length === 0) return;

      const local = readWishlist();
      const merged = Array.from(new Set([...local, ...serverList]));
      writeWishlist(merged);
    } catch (e) {}
  }

  /* ---------- Heart button handling (global) ---------- */

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const value = (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    });
    return value;
  }

  function updateAllHearts() {
    const list = readWishlist();
    document.querySelectorAll('[data-avora-wishlist-toggle]').forEach((btn) => {
      const handle = btn.getAttribute('data-product-handle');
      const pressed = list.includes(handle);
      btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      btn.setAttribute(
        'aria-label',
        pressed
          ? btn.getAttribute('aria-label')?.replace('Save', 'Remove') || 'Remove from wishlist'
          : btn.getAttribute('aria-label')?.replace('Remove', 'Save') || 'Save to wishlist'
      );
      const label = btn.querySelector('[data-avora-wishlist-label]');
      if (label) label.textContent = pressed ? 'Saved' : 'Save';
    });
  }

  /* ── Floating hearts animation ── */
  function spawnFloatingHearts(btn) {
    var count = 7;
    for (var i = 0; i < count; i++) {
      spawnHeart(btn, i * 50, i);
    }
  }

  function spawnHeart(btn, delay, index) {
    /* Pre-compute all random values OUTSIDE the animation loop */
    var size = 9 + Math.random() * 7;
    var drift = (Math.random() - 0.5) * 30;
    var startX = (Math.random() - 0.5) * 10;
    var travelY = 80 + Math.random() * 50;
    var duration = 900 + Math.random() * 400;
    var wobbleSpeed = 1.2 + Math.random() * 0.8;
    var rotateMax = (Math.random() - 0.5) * 30;
    /* Slight red hue variation */
    var colors = ['#E25555', '#E86060', '#D94A4A', '#F06B6B', '#CC4444'];
    var color = colors[index % colors.length];

    setTimeout(function () {
      var el = document.createElement('span');
      el.className = 'avora-wh__float';
      el.innerHTML =
        '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="' + color + '" stroke="none" opacity="0.9">' +
        '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
        '</svg>';
      el.style.cssText = 'left:50%;top:50%;opacity:0;';
      btn.appendChild(el);

      var start = null;

      function frame(ts) {
        if (!start) start = ts;
        var t = Math.min((ts - start) / duration, 1);

        /* Smooth ease-out-quart */
        var ease = 1 - Math.pow(1 - t, 4);

        var y = -ease * travelY;
        var x = startX + Math.sin(t * Math.PI * wobbleSpeed) * drift;
        var rot = Math.sin(t * Math.PI) * rotateMax;

        /* Scale: quick pop in → hold → gentle shrink */
        var s;
        if (t < 0.1) s = t / 0.1;
        else if (t < 0.6) s = 1;
        else s = 1 - ((t - 0.6) / 0.4) * 0.5;

        /* Opacity: instant in → long hold → smooth out */
        var o;
        if (t < 0.08) o = t / 0.08;
        else if (t < 0.65) o = 1;
        else o = 1 - (t - 0.65) / 0.35;

        el.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0) scale(' + s.toFixed(2) + ') rotate(' + rot.toFixed(1) + 'deg)';
        el.style.opacity = o.toFixed(2);

        if (t < 1) requestAnimationFrame(frame);
        else el.remove();
      }

      requestAnimationFrame(frame);
    }, delay);
  }

  function onHeartClick(e) {
    const btn = e.target.closest('[data-avora-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    const isNowSaved = toggleWishlist(handle);

    btn.setAttribute('aria-pressed', isNowSaved ? 'true' : 'false');

    /* Clear any running animation */
    btn.classList.remove('is-saving', 'is-removing');
    void btn.offsetWidth;

    if (isNowSaved) {
      btn.classList.add('is-saving');
      spawnFloatingHearts(btn);
      setTimeout(() => btn.classList.remove('is-saving'), 500);
    } else {
      btn.classList.add('is-removing');
      setTimeout(() => btn.classList.remove('is-removing'), 400);
    }

    window.dispatchEvent(
      new CustomEvent('avora:wishlist:change', {
        detail: { handle, added: isNowSaved, total: readWishlist().length },
      })
    );
  }

  document.addEventListener('click', onHeartClick);

  mergeServerWishlist();
  updateAllHearts();

  window.addEventListener('avora:wishlist:change', updateAllHearts);

  /* ---------- Header badge ---------- */
  function updateHeaderBadge() {
    const count = readWishlist().length;
    const badge = document.querySelector('[data-wishlist-count]');
    if (!badge) return;
    badge.textContent = count;
    badge.hidden = count === 0;
  }
  updateHeaderBadge();
  window.addEventListener('avora:wishlist:change', updateHeaderBadge);

  /* ---------- Wishlist page rendering ---------- */

  const page = document.querySelector('[data-avora-wishlist-page]');
  if (!page) return;

  const grid = page.querySelector('[data-wishlist-grid]');
  const empty = page.querySelector('[data-wishlist-empty]');
  const loading = page.querySelector('[data-wishlist-loading]');
  const heading = page.querySelector('[data-wishlist-heading]');
  const subtext = page.querySelector('[data-wishlist-subtext]');
  const actions = page.querySelector('[data-wishlist-actions]');
  const shareBtn = page.querySelector('[data-wishlist-share]');

  function showEmpty() {
    if (grid) grid.hidden = true;
    if (loading) loading.hidden = true;
    if (actions) actions.hidden = true;
    if (empty) empty.hidden = false;
    if (heading) heading.textContent = 'Your wishlist.';
    if (subtext) subtext.textContent = '';
  }

  function showLoading() {
    if (loading) loading.hidden = false;
    if (grid) grid.hidden = true;
    if (empty) empty.hidden = true;
  }

  function showGrid(count) {
    if (loading) loading.hidden = true;
    if (empty) empty.hidden = true;
    if (grid) grid.hidden = false;
    if (actions) actions.hidden = false;
    if (heading) heading.textContent = 'Your wishlist.';
    if (subtext) {
      subtext.textContent = `${count} ${count === 1 ? 'item' : 'items'} saved for later.`;
    }
  }

  function renderCard(product) {
    if (!product) return '';

    const price = formatMoney(product.price_min);
    const isSoldOut = !product.available;

    return `
      <article class="avora-wishlist__card ${isSoldOut ? 'avora-wishlist__card--soldout' : ''}" data-handle="${escapeHtml(product.handle)}">
        <div class="avora-wishlist__card-image-wrap">
          <a href="${escapeHtml(product.url)}">
            <img class="avora-wishlist__card-image" src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">
          </a>
          ${isSoldOut ? '<span class="avora-wishlist__card-soldout">Sold out</span>' : ''}
          <button type="button" class="avora-wishlist__card-remove" data-remove-handle="${escapeHtml(product.handle)}" aria-label="Remove ${escapeHtml(product.title)} from wishlist">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="avora-wishlist__card-body">
          <a class="avora-wishlist__card-title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <p class="avora-wishlist__card-price">${escapeHtml(price)}</p>
          ${
            isSoldOut
              ? `<button type="button" class="avora-wishlist__card-cta avora-wishlist__card-cta--notify" data-notify-handle="${escapeHtml(product.handle)}">Notify me</button>`
              : `<button type="button" class="avora-wishlist__card-cta" data-add-cart-variant="${escapeHtml(product.first_available_variant_id)}" data-add-cart-handle="${escapeHtml(product.handle)}">Add to cart</button>`
          }
        </div>
      </article>
    `;
  }

  async function renderWishlistPage() {
    const handles = readWishlist();
    if (handles.length === 0) {
      showEmpty();
      return;
    }

    showLoading();

    const results = await Promise.all(handles.map(hydrateProduct));
    const valid = results.filter(Boolean);

    if (valid.length < handles.length) {
      const validHandles = valid.map((p) => p.handle);
      writeWishlist(validHandles);
    }

    if (valid.length === 0) {
      showEmpty();
      return;
    }

    grid.innerHTML = valid.map(renderCard).join('');
    showGrid(valid.length);

    grid.querySelectorAll('[data-remove-handle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.getAttribute('data-remove-handle');
        const card = btn.closest('.avora-wishlist__card');

        if (card) {
          card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
          card.style.opacity = '0';
          card.style.transform = 'scale(0.95)';
        }

        setTimeout(() => {
          removeFromWishlist(handle);
          renderWishlistPage();
        }, 300);
      });
    });

    grid.querySelectorAll('[data-add-cart-variant]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.getAttribute('data-add-cart-variant');
        if (!variantId || variantId === 'null') return;

        btn.disabled = true;
        btn.textContent = 'Adding…';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });

          if (!res.ok) throw new Error('Add to cart failed');

          btn.textContent = 'Added ✓';
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = 'Add to cart';
          }, 1500);

          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
          window.dispatchEvent(new CustomEvent('cart:update'));
        } catch (err) {
          btn.textContent = 'Try again';
          btn.disabled = false;
        }
      });
    });

    grid.querySelectorAll('[data-notify-handle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.getAttribute('data-notify-handle');
        window.dispatchEvent(
          new CustomEvent('avora:notify-me', { detail: { handle } })
        );
      });
    });
  }

  /* ---------- Share wishlist ---------- */

  shareBtn?.addEventListener('click', async () => {
    const handles = readWishlist();
    if (handles.length === 0) return;

    const params = new URLSearchParams();
    handles.forEach((h) => params.append('h', h));
    const url = `${window.location.origin}/pages/wishlist?${params.toString()}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Avora Home wishlist',
          text: 'Take a look at what I have my eye on.',
          url,
        });
        return;
      } catch (e) {}
    }

    try {
      await navigator.clipboard.writeText(url);
      const original = shareBtn.textContent;
      shareBtn.textContent = 'Link copied ✓';
      setTimeout(() => {
        shareBtn.textContent = original;
      }, 2000);
    } catch (e) {
      window.prompt('Copy this link:', url);
    }
  });

  /* ---------- URL-imported wishlist (from a share link) ---------- */

  const urlParams = new URLSearchParams(window.location.search);
  const importedHandles = urlParams.getAll('h');
  if (importedHandles.length > 0) {
    const current = readWishlist();
    const merged = Array.from(new Set([...current, ...importedHandles]));
    writeWishlist(merged);
    window.history.replaceState({}, '', '/pages/wishlist');
  }

  /* ---------- Initial render ---------- */

  renderWishlistPage();

  window.addEventListener('avora:wishlist:change', renderWishlistPage);
})();
